import { useEffect, useState } from "react";
import { useBeforeunload } from 'react-beforeunload';
import { useDispatch, useSelector } from "react-redux";
import { config } from "node-config-ts";
import {
    AXNUIFormPanelHeaderContainer
} from "@iddataweb/idw-common-ui/containers";
import { Logger } from "@utils/logger";
import {
    getFormPanelLayout,
    getFormPanelTheme,
    getIDPName,
    getLogoPosition,
    getLogoUrl,
    getPageTitle,
    getRPName
} from "@redux/selectors/verification-configuration-selectors";
import { getLanguageContent, getLanguageState } from "@redux/selectors/language-selectors";
import { getCommonState } from "@redux/selectors/common-selectors";
import { DisclaimerContainer } from "./disclaimer/disclaimer-container";
import { AttributeListContainer } from "./attribute-list/attribute-list-container";
import { PreAttributeListContainer } from "./pre-attribute-list-container/pre-attribute-list-container";
import { PostSubmitButtonContainer } from "./post-submit-button-container/post-submit-button-container";
import styles from "./verification-form.less";
import { resetState } from "../../redux/actions/root-actions";

type VerificationFormProps = {
    useThemedUI: boolean;
}

export const VerificationForm = (props: VerificationFormProps) => {
    const { useThemedUI } = props;

    const [canShowPolicyDecisionRetryMessage, setCanShowPolicyDecisionRetryMessage] = useState(true);

    const dispatch = useDispatch();
    const pageTitle = useSelector(getPageTitle);
    const languageState = useSelector(getLanguageState);
    const commonState = useSelector(getCommonState);
    const content = useSelector(getLanguageContent);
    const idpName = useSelector(getIDPName);
    const relyingPartyName = useSelector(getRPName);
    const logoUrl = useSelector(getLogoUrl);
    const logoPosition = useSelector(getLogoPosition);
    const formPanelTheme = useSelector(getFormPanelTheme);
    const formPanelLayout = useSelector(getFormPanelLayout);
   
    useEffect(() => {
        document.title = pageTitle;
    }, [pageTitle]);

    useEffect(() => {
        if (languageState.activeLanguage) {
            document.documentElement.setAttribute('lang', languageState.activeLanguage);
        }
    }, [languageState.activeLanguage]);

    useEffect(() => {
        const handleUnload = () => {
            dispatch(resetState());
        };
        window.addEventListener("unload", handleUnload);

        return () => {
            window.removeEventListener("unload", handleUnload);
        }
    }, []);

    useBeforeunload(() => {
        if (!config.REACT_APP_DISABLE_NAVIGATION_WARNING) {
            Logger.info(`[CompactLayout] Navigation away allowed to redirectUrl?: ${!!commonState.shouldRedirect}`);
            if (commonState.shouldRedirect) {
                Logger.info(`[CompactLayout] Navigation away allowed to redirectUrl: ${!!commonState.shouldRedirect}`);
            } else {
                Logger.info('[CompactLayout] Navigation away should have been prevented if the user has interacted with the page');
                return `Are you sure you want to navigate away from this page? \n
				Navigating away will invalidate your verification session and may cause errors. \n
				Press OK to leave or Cancel to stay on the current page and finish the verification.`;
            }
        } else {
            Logger.info('[CompactLayout] Navigation away enabled');
        }

        return null;
    });

    return (
        <div className={styles.verificationForm}>
            <AXNUIFormPanelHeaderContainer
                formPanelContent={content.formPanel}
                formPanelLayout={formPanelLayout}
                formPanelTheme={formPanelTheme}
                idpName={idpName}
                logoPosition={logoPosition}
                logoUrl={logoUrl}
                relyingPartyName={relyingPartyName}
                showDescription={true}
                showTitle={true}
                useThemedUI={useThemedUI}
            />
            <DisclaimerContainer />
            <div className={"card-body"}>
                <PreAttributeListContainer
                    useThemedUI={useThemedUI}
                    canShowPolicyDecisionRetryMessage={canShowPolicyDecisionRetryMessage}
                />
                <AttributeListContainer
                    useThemedUI={useThemedUI}
                    canShowPolicyDecisionRetryMessage={canShowPolicyDecisionRetryMessage}
                    setCanShowPolicyDecisionRetryMessage={setCanShowPolicyDecisionRetryMessage}
                />
                <PostSubmitButtonContainer useThemedUI={useThemedUI} />
            </div>
        </div >
    );
};